import Organizations from "api/lib/data/Organizations";
import Users from "api/lib/data/Users";
import * as React from "react";
import { connect } from "react-redux";
import { Redirect, withRouter } from "react-router-dom";
import { compose } from "recompose";
import { Message } from "semantic-ui-react";
import { IRoles, SESSION_EVENTS } from "src/reducers/session";
import * as routes from "../../constants/routes";
import { firebaselogin } from "../../firebase/firebaselogin";
import { Event, IListener } from "../Common/Base";

export interface UserStatus {
  lastSeen: number;
  online: boolean;
}

export interface UsersStatus {
  [id: string]: UserStatus;
}

interface IInterfaceProps {
  history?: any;
  authUser?: any;
}

interface IWithAuthorizationState {
  roles: IRoles;
  verifyRoles: boolean;
  loadingRoles: boolean;
  hideWelcomeMessage: boolean;
  orgConfig: any;
  orgId: string;
  authUser: any;
  usersStatus: UsersStatus;
  listeners: IListener[];
}

export const withAuthorization = (condition: any) => (Component: any) => {
  class WithAuthorization extends React.Component<
    IInterfaceProps,
    IWithAuthorizationState
  > {
    constructor(props: IInterfaceProps) {
      super(props);
      this.state = {
        roles: null,
        verifyRoles: true,
        loadingRoles: true,
        hideWelcomeMessage: false,
        orgConfig: null,
        orgId: null,
        authUser: null,
        usersStatus: {},
        listeners: [],
      };
    }

    componentWillUnmount() {
      this.removeListeners();
    }

    private removeListeners = () => {
      const { listeners } = this.state;
      listeners.forEach((thisListener: IListener) => {
        thisListener.ref.off(thisListener.event, thisListener.listener);
      });
    };

    public componentDidMount() {
      const { onSetOrgIdAndRoles, match }: any = this.props;
      firebaselogin.auth().onAuthStateChanged((authUser: firebase.User) => {
        if (!condition(authUser)) {
          localStorage.setItem("redirectUrl", match.url);
          this.props.history.push(routes.SIGN_IN);
        } else {
          if (match && match.params) {
            const { orgId } = match.params;
            if (orgId) {
              this.setState({ orgId });
              onSetOrgIdAndRoles(null, orgId, null);
              const orgs: Organizations = new Organizations();
              orgs
                .getConfig(orgId)
                .once(
                  "value",
                  (configSnapshot: firebase.database.DataSnapshot) => {
                    const config: any = configSnapshot.val();
                    this.setState({ orgConfig: config });
                  }
                );
              orgs
                .getAuthRoles(orgId, authUser.uid)
                .once(
                  "value",
                  (authRolesSnapshot: firebase.database.DataSnapshot) => {
                    const roles: IRoles = authRolesSnapshot.val();
                    this.setState({ roles });
                    this.setPresence(authUser, orgId, (usersStatus) => {
                      onSetOrgIdAndRoles(
                        roles,
                        orgId,
                        this.state.orgConfig,
                        usersStatus
                      );
                      this.setState({ loadingRoles: false });
                    });
                  }
                );
            } else {
              this.setState({ verifyRoles: false, loadingRoles: false });
            }
          } else {
            this.setState({ verifyRoles: false, loadingRoles: false });
          }
        }
      });
    }

    private setPresence(authUser, orgId, callback) {
      const { listeners } = this.state;
      const users = new Users(authUser.uid);
      const myConnectionsRef = users.getConnections(orgId, authUser.uid);
      const lastOnlineRef = users.getLastOnlineTimes(orgId, authUser.uid);
      const connectedRef = users.getDotInfoConnected();

      const connectedRefListener = connectedRef.on(Event.Value, (snap) => {
        if (snap.val() === true) {
          const con = myConnectionsRef.push();
          con.onDisconnect().remove();
          con.set(true);
          lastOnlineRef
            .onDisconnect()
            .set(firebaselogin.database.ServerValue.TIMESTAMP);
        }
      });
      listeners.push({
        ref: connectedRef,
        event: Event.Value,
        listener: connectedRefListener,
      });

      const usersListRef = new Organizations().getUserIds(orgId);
      const usersListRefListener = usersListRef.on(Event.Value, (snap) => {
        const preferenceUserRef = users.getPresenceUsers(orgId);
        const preferenceUserRefListener = preferenceUserRef.on(
          Event.Value,
          (presenceUsersSnap) => {
            const usersStatus = {};
            const presenceeUsers = presenceUsersSnap.val();
            Object.keys(presenceeUsers).forEach((presenceUserId) => {
              const presenceUser = presenceeUsers[presenceUserId];
              const lastOnline = presenceUser.lastOnline
                ? presenceUser.lastOnline
                : 0;
              const online = presenceUser.connections ? true : false;
              usersStatus[presenceUserId] = { online, lastOnline };
            });
            callback(usersStatus);
          }
        );
        listeners.push({
          ref: preferenceUserRef,
          event: Event.Value,
          listener: preferenceUserRefListener,
        });
      });
      listeners.push({
        ref: usersListRef,
        event: Event.Value,
        listener: usersListRefListener,
      });
    }

    public render() {
      const {
        roles,
        verifyRoles,
        loadingRoles,
        hideWelcomeMessage,
        orgConfig,
        orgId,
      } = this.state;
      const { match, showWelcomeMessage }: any = this.props;
      if (!loadingRoles && this.props.authUser) {
        if (!verifyRoles || (orgId && roles && orgConfig)) {
          return (
            <React.Fragment>
              {!hideWelcomeMessage && showWelcomeMessage && orgConfig && (
                <div
                  style={{
                    position: "absolute",
                    top: "15px",
                    left: "15px",
                    width: "calc(100% - 30px)",
                    zIndex: 2000000,
                  }}
                >
                  <Message
                    style={{
                      opacity: "0.8",
                      textAlign: "center",
                      padding: "8px",
                    }}
                    positive={true}
                    onDismiss={this.hideJoinMessage}
                    header={`Welcome to ${orgConfig.name}`}
                  />
                </div>
              )}
              <Component />
            </React.Fragment>
          );
        } else {
          if (match.path.indexOf("/join/") === -1) {
            return <Redirect to={routes.ERROR} />;
          } else {
            return <React.Fragment>{orgId && <Component />}</React.Fragment>;
          }
        }
      } else {
        return null;
      }
    }

    private hideJoinMessage = (event: any, data: any) => {
      this.setState({ hideWelcomeMessage: true });
    };
  }

  const mapStateToProps = (state: any) => {
    return {
      authUser: state.sessionState.authUser,
      showWelcomeMessage: state.sessionState.showWelcomeMessage,
    };
  };

  const mapDispatchToProps = (dispatch: any) => ({
    onSetOrgIdAndRoles: (
      roles: IRoles,
      orgId: string,
      orgConfig: any,
      usersStatus: any
    ) =>
      dispatch({
        type: SESSION_EVENTS.ORG_ID_AND_ROLES_SET,
        roles,
        orgId,
        orgConfig,
        usersStatus,
      }),
  });

  return compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
  )(WithAuthorization);
};



// WEBPACK FOOTER //
// ./src/components/Session/withAuthorization.tsx